import { Component, EventEmitter, Input, OnInit, OnDestroy, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { SectionDetailsService } from './section-details.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import * as _ from 'lodash-es';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-section-details',
    templateUrl: './section-details.component.html',
    styleUrls: ['./section-details.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SectionDetailsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  filterParams: any = {};
  baseURLs: any = {};
  rowData: any = {};

  @Input('data-row')
  set _data_row(dataRow: string){
    try{
      let data: any = safeJsonInput<any>(dataRow, null, 'section-details.parse1');
      if(Object.keys(data).length > 0){
        this.dataRow = data;
        this.tryInit();
      }
    }catch(e){
      console.log("Data row input not yet stable. Still waiting to get resolved.");
    }
  }
  dataRow: any = {};

  @Input('source-row')
  set _source_row(sourceRow: string){
    try{
      let data: any = safeJsonInput<any>(sourceRow, null, 'section-details.parse2');
      if(Object.keys(data).length > 0){
        this.sourceRow = data;
        this.tryInit();
      }
    }catch(e){
      console.log("Source row input not yet stable. Still waiting to get resolved.");
    }
  }
  sourceRow: any = {};

  // Component properties following F7004 pattern
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};

  crudLoading: boolean = false;
  logged_in_user: string = '';
  sectionFormData: any[] = [];
  sectionName: string = '';
  occurrence: string = '';
  tax_year: string = '';
  scenario: string = '';
  sel_multiplier: any = null;
  minimise: boolean = false;
  percentDrpDwnData: any[] = [
    {"LABEL":"100%", VALUE:100},
    {"LABEL":"80% if the farmland was disposed of within the 6th year", VALUE:80},
    {"LABEL":"60% if the farmland was disposed of within the 7th year", VALUE:60},
    {"LABEL":"40% if the farmland was disposed of within the 8th year", VALUE:40},
    {"LABEL":"20% if the farmland was disposed of within the 9th year", VALUE:20}
  ];

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private sectionDetailsService: SectionDetailsService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = gc;
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.GENERAL_CONFIG = { ...(header.generalConfigData || {}), base_url: gc.base_url || gc.api || '' };
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.detectChanges();
  }

  private resolveApiBase(): string {
    return this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || this.baseURLs?.base_url || '';
  }

  private tryInit(): void {
    const baseOk = !!this.resolveApiBase();
    const hasSourceRow = this.sourceRow && Object.keys(this.sourceRow).length > 0;
    const hasDataRow = this.dataRow && Object.keys(this.dataRow).length > 0;
    if (!baseOk || !hasSourceRow || !hasDataRow) return;
    this.init();
  }

  init(): void {
    const hasFilterParams = this.filterParams && Object.keys(this.filterParams).length > 0;
    const hasGeneralConfig = this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0;
    const hasSourceRow = this.sourceRow && Object.keys(this.sourceRow).length > 0;
    const hasDataRow = this.dataRow && Object.keys(this.dataRow).length > 0;

    if (hasFilterParams && hasGeneralConfig && hasSourceRow && hasDataRow) {
      this.initializeData();
    }
  }

  initializeData(): void {
    // Get logged in user from USER_SETTINGS
    if (this.USER_SETTINGS?.user?.sso_id) {
      this.logged_in_user = this.USER_SETTINGS.user.sso_id;
    }

    // Get section details from sourceRow
    this.sectionName = this.sourceRow?.sectionVal || '';
    this.occurrence = this.sourceRow?.occurrence || '';
    this.tax_year = this.sourceRow?.tax_year || this.filterParams?.tax_year || this.rowData?.TAX_YEAR || '';
    this.scenario = this.sourceRow?.scenario || this.filterParams?.scenario || this.rowData?.SCENARIO || '';

    // Fetch section modal data
    this.fetchSectionModalData();
  }

  fetchSectionModalData(): void {
    // Follow exact AngularJS fetchSectionModalData function behavior
    const combinationKey = this.rowData?.COMBINATION_KEY || this.filterParams?.combination_key || '';

    this.sectionDetailsService.fetchSectionDetails(
      this.resolveApiBase(),
      this.sectionName,
      this.logged_in_user,
      this.occurrence,
      this.tax_year,
      this.scenario,
      combinationKey
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        console.log('fetchSectionDetails::::::::::::', data);
        if (data.callSuccess === "1") {
          this.sectionFormData = data.jsonObject || [];

          // Handle section 1250 custom check
          if (this.sectionName === "1250") {
            const line_26a_index = this.sectionFormData.findIndex((x: any) => x.ATTRIB_SHORT_CODE === "LINE_26a_ADDL_DEPR_19");
            if (line_26a_index >= 0) {
              this.customCheckForSection1250(line_26a_index);
            }
          }
          // Handle section 1252 multiplier
          else if (this.sectionName === "1252") {
            const line_27a_multiplier_index = this.sectionFormData.findIndex((x: any) => x.ATTRIB_SHORT_CODE === "LINE_27a_MULTIPLIER_19");
            if (line_27a_multiplier_index >= 0) {
              const line_27a_multiplier_val = this.sectionFormData[line_27a_multiplier_index].ATTRIB_VALUE;
              const multplier_drpdwn_sel_index = this.percentDrpDwnData.findIndex((x: any) => x.VALUE == line_27a_multiplier_val);
              if (multplier_drpdwn_sel_index >= 0) {
                this.sel_multiplier = this.percentDrpDwnData[multplier_drpdwn_sel_index];
              }
            }
          }
        } else {
          this.gtwSnackbarService.openSnackBar(data.errorMessage || 'Error fetching section details', 'gtw-snackbar--red');
        }
      },
      (error: any) => {
        console.error('Error fetching section details:', error);
        this.gtwSnackbarService.openSnackBar('Error occurred while fetching section details. Please try again.', 'gtw-snackbar--red');
      }
    );
  }

  customCheckForSection1250(index: number): void {
    // Follow exact AngularJS customCheckForSection1250 function behavior
    if (this.sectionFormData[index].ATTRIB_SHORT_CODE === 'LINE_26a_ADDL_DEPR_19') {
      const gain_loss_index = this.dataRow?.Line_data_occ?.findIndex((x: any) => x.ATTRIB_SHORT_CODE === "GAIN_LOSS_19");
      if (gain_loss_index >= 0) {
        const gain_loss_amt = this.dataRow.Line_data_occ[gain_loss_index].ATTRIB_VALUE;
        const line_26a_amt = this.sectionFormData[index].ATTRIB_VALUE;
        const line_26d_index = this.sectionFormData.findIndex((x: any) => x.ATTRIB_SHORT_CODE === "LINE_26d_ADDL_DEPR_19");

        if (line_26d_index >= 0) {
          if (parseFloat(gain_loss_amt) < parseFloat(line_26a_amt)) {
            this.sectionFormData[line_26d_index].IS_EDITABLE = "N";
            this.sectionFormData[line_26d_index].ATTRIB_VALUE = 0;
          } else {
            this.sectionFormData[line_26d_index].IS_EDITABLE = "Y";
          }
        }
      }
    }
  }

  valuechanged(index: number): void {
    // Follow exact AngularJS valuechanged function behavior
    this.sectionFormData[index].IS_CHANGED_FLAG = "Y";
    this.customCheckForSection1250(index);
  }

  drpDownChange(current_row: any, index: number): void {
    // Follow exact AngularJS drpDownChange function behavior
    if (this.sel_multiplier) {
      this.sectionFormData[index].ATTRIB_VALUE = this.sel_multiplier.VALUE;
      this.sectionFormData[index].IS_CHANGED_FLAG = "Y";
    }
  }

  cancel(): void {
    // Following F7004 pattern - dispatch CustomEvent to close modal
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true
    });

    if (document.querySelector('gtw-section-details-ce')) {
      document.querySelector('gtw-section-details-ce')?.dispatchEvent(updateEvent);
    }
  }

  save(): void {
    const baseUrl = this.resolveApiBase();
    if (this.crudLoading || !baseUrl) return;

    // Follow exact AngularJS save function behavior
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('The transaction is still in progress', 'gtw-snackbar--yellow');
      return;
    }

    this.crudLoading = true;

    // Build clob_settings exactly like AngularJS
    const clob_settings = [{
      sso_id: this.logged_in_user,
      screen_key: this.globalService?.screen_key || ''
    }];

    // Add OCCURRENCE to each sectionFormData item
    for (let i = 0; i < this.sectionFormData.length; i++) {
      this.sectionFormData[i].OCCURRENCE = this.occurrence;
    }

    const message = "Section Details are successfully updated.";

    // Call service method like AngularJS
    this.sectionDetailsService.saveSection(
      this.resolveApiBase(),
      this.sectionFormData,
      clob_settings,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.crudLoading = false;

        // AngularJS checks result.data.errorMessage
        const response = result.data || result;
        if (response.errorMessage && response.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
        } else {
          // Show success message (equivalent to AlertService.add("success", message, 4000))
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');

          // CRITICAL: Emit 'refresh' string for AngularJS modal close
          this.requestGridUpdate.emit('refresh');
          this.cancel();
        }
      },
      (error: any) => {
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        console.error('Save section error:', error);
      }
    );
  }
}

